import React, { useState, useEffect, useRef } from 'react';
import './WorkTimeCalculator.css';

const WorkTimeCalculator = () => {
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [workProgress, setWorkProgress] = useState(0);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [preReminderMinutes, setPreReminderMinutes] = useState(5);
  const [isPWA, setIsPWA] = useState(false);
  
  const intervalRef = useRef(null);
  const preReminderTimeoutRef = useRef(null);
  const finalReminderTimeoutRef = useRef(null);
  const preReminderSentRef = useRef(false);
  const finalReminderSentRef = useRef(false);

  // Funkcja obliczająca godzinę wyjścia na podstawie dnia tygodnia
  const calculateEndTime = (start) => {
    const startDate = new Date(`2000-01-01T${start}`);
    const dayOfWeek = new Date().getDay(); // 0 = niedziela, 1 = poniedziałek, etc.
    
    let workHours = 0;
    let workMinutes = 0;
    
    switch (dayOfWeek) {
      case 1: // Poniedziałek
        workHours = 9;
        workMinutes = 30;
        break;
      case 2: // Wtorek
      case 3: // Środa
      case 4: // Czwartek
        workHours = 8;
        workMinutes = 0;
        break;
      case 5: // Piątek
        workHours = 6;
        workMinutes = 30;
        break;
      default:
        // Weekend - domyślnie 8 godzin
        workHours = 8;
        workMinutes = 0;
    }
    
    const endDate = new Date(startDate);
    endDate.setHours(startDate.getHours() + workHours);
    endDate.setMinutes(startDate.getMinutes() + workMinutes);
    
    const hours = String(endDate.getHours()).padStart(2, '0');
    const minutes = String(endDate.getMinutes()).padStart(2, '0');
    
    return `${hours}:${minutes}`;
  };

  // Wykrywanie czy aplikacja działa w trybie PWA (standalone)
  useEffect(() => {
    const checkPWA = () => {
      // Sprawdź czy jesteśmy w standalone mode (PWA)
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          window.navigator.standalone ||
                          document.referrer.includes('android-app://');
      setIsPWA(isStandalone);
      
      // Dodaj klasę do body dla stylów PWA
      if (isStandalone) {
        document.body.classList.add('pwa-mode');
      } else {
        document.body.classList.remove('pwa-mode');
      }
    };
    
    checkPWA();
    // Sprawdź ponownie przy zmianie orientacji/rozmiaru
    window.addEventListener('resize', checkPWA);
    return () => window.removeEventListener('resize', checkPWA);
  }, []);

  // Przywracanie godziny rozpoczęcia z localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('workTimeData');
    if (savedData) {
      try {
        const { startTime: savedStartTime, date: savedDate } = JSON.parse(savedData);
        const today = new Date().toDateString();
        
        // Przywróć tylko jeśli to ten sam dzień
        if (savedDate === today && savedStartTime) {
          setStartTime(savedStartTime);
          const calculatedEndTime = calculateEndTime(savedStartTime);
          setEndTime(calculatedEndTime);
        } else {
          // Wyczyść stare dane
          localStorage.removeItem('workTimeData');
        }
      } catch (error) {
        console.error('Błąd przy odczycie danych z localStorage:', error);
      }
    }
  }, []);

  // Zapisywanie godziny rozpoczęcia do localStorage
  useEffect(() => {
    if (startTime) {
      const today = new Date().toDateString();
      localStorage.setItem('workTimeData', JSON.stringify({
        startTime,
        date: today
      }));
    }
  }, [startTime]);

  // Sprawdź uprawnienia do powiadomień przy starcie
  useEffect(() => {
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, []);

  // Funkcja żądania uprawnień do powiadomień
  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      alert('Twoja przeglądarka nie obsługuje powiadomień.');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      
      if (permission === 'granted') {
        alert('Powiadomienia zostały włączone!');
      } else if (permission === 'denied') {
        alert('Powiadomienia zostały zablokowane. Możesz je włączyć w ustawieniach przeglądarki.');
      }
    } catch (error) {
      console.error('Błąd podczas żądania uprawnień:', error);
    }
  };

  // Funkcja wysyłająca powiadomienie (używana poza useEffect)
  const sendNotification = (title, body, tag = 'work-reminder') => {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    try {
      const notification = new Notification(title, {
        body: body,
        tag: tag,
        icon: '/logo192.png',
        badge: '/logo192.png',
        requireInteraction: false,
        silent: false
      });

      // Automatyczne zamknięcie po 5 sekundach
      setTimeout(() => {
        notification.close();
      }, 5000);
    } catch (error) {
      console.error('Błąd podczas wysyłania powiadomienia:', error);
    }
  };

  // Funkcja obliczająca całkowity czas pracy w minutach dla danego dnia
  const getTotalWorkMinutes = () => {
    const dayOfWeek = new Date().getDay();
    switch (dayOfWeek) {
      case 1: return 9.5 * 60; // Poniedziałek
      case 2:
      case 3:
      case 4: return 8 * 60; // Wtorek-Czwartek
      case 5: return 6.5 * 60; // Piątek
      default: return 8 * 60; // Weekend
    }
  };

  // Funkcja obliczająca pozostały czas i postęp
  const calculateTimeRemaining = (endTimeStr, startTimeStr) => {
    const now = new Date();
    const [endHours, endMinutes] = endTimeStr.split(':').map(Number);
    const [startHours, startMinutes] = startTimeStr.split(':').map(Number);
    
    const endDate = new Date();
    endDate.setHours(endHours, endMinutes, 0, 0);
    
    const startDate = new Date();
    startDate.setHours(startHours, startMinutes, 0, 0);
    
    // Jeśli godzina wyjścia już minęła dzisiaj, ustaw na jutro
    if (endDate <= now) {
      endDate.setDate(endDate.getDate() + 1);
    }
    
    const diff = endDate - now;
    const hoursRemaining = Math.floor(diff / (1000 * 60 * 60));
    const minutesRemaining = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    const secondsRemaining = Math.floor((diff % (1000 * 60)) / 1000);
    
    // Oblicz postęp dnia pracy (w procentach)
    const totalWorkMs = getTotalWorkMinutes() * 60 * 1000;
    const elapsedMs = now - startDate;
    const progress = Math.min(Math.max((elapsedMs / totalWorkMs) * 100, 0), 100);
    
    return {
      hours: hoursRemaining,
      minutes: minutesRemaining,
      seconds: secondsRemaining,
      totalMs: diff,
      endDate: endDate,
      progress: progress
    };
  };

  // Główny efekt obsługujący timer i powiadomienia
  useEffect(() => {
    if (!startTime || !endTime) {
      return;
    }

    // Reset flagów powiadomień
    preReminderSentRef.current = false;
    finalReminderSentRef.current = false;

    // Czyszczenie poprzednich timeoutów i interwałów
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    if (preReminderTimeoutRef.current) {
      clearTimeout(preReminderTimeoutRef.current);
    }
    if (finalReminderTimeoutRef.current) {
      clearTimeout(finalReminderTimeoutRef.current);
    }

    // Funkcja wysyłająca powiadomienie (z dostępem do aktualnego permission)
    const sendNotificationSafe = (title, body, tag = 'work-reminder') => {
      if (Notification.permission !== 'granted') {
        return;
      }

      try {
        const notification = new Notification(title, {
          body: body,
          tag: tag,
          icon: '/logo192.png',
          badge: '/logo192.png',
          requireInteraction: false,
          silent: false
        });

        // Automatyczne zamknięcie po 5 sekundach
        setTimeout(() => {
          notification.close();
        }, 5000);
      } catch (error) {
        console.error('Błąd podczas wysyłania powiadomienia:', error);
      }
    };

    // Aktualizacja timera co sekundę
    const updateTimer = () => {
      const remaining = calculateTimeRemaining(endTime, startTime);
      setTimeRemaining(remaining);
      setWorkProgress(remaining.progress);

      // Sprawdź czy czas na pre-przypomnienie
      if (!preReminderSentRef.current && preReminderMinutes > 0 && remaining.totalMs <= preReminderMinutes * 60 * 1000 && remaining.totalMs > 0) {
        preReminderSentRef.current = true;
        sendNotificationSafe(
          'Przypomnienie o wyjściu',
          `Zostało ${preReminderMinutes} minut do końca pracy!`,
          'pre-reminder'
        );
      }

      // Sprawdź czy czas na finalne powiadomienie
      if (!finalReminderSentRef.current && remaining.totalMs <= 0) {
        finalReminderSentRef.current = true;
        sendNotificationSafe(
          'Czas wyjścia!',
          'To już czas, aby zakończyć pracę!',
          'final-reminder'
        );
      }
    };

    // Pierwsze obliczenie
    updateTimer();

    // Ustawienie interwału
    intervalRef.current = setInterval(updateTimer, 1000);

    // Ustawienie timeoutów dla powiadomień (backup)
    const remaining = calculateTimeRemaining(endTime, startTime);
    
    if (preReminderMinutes > 0 && remaining.totalMs > preReminderMinutes * 60 * 1000) {
      const preReminderDelay = remaining.totalMs - (preReminderMinutes * 60 * 1000);
      preReminderTimeoutRef.current = setTimeout(() => {
        if (!preReminderSentRef.current) {
          preReminderSentRef.current = true;
          sendNotificationSafe(
            'Przypomnienie o wyjściu',
            `Zostało ${preReminderMinutes} minut do końca pracy!`,
            'pre-reminder'
          );
        }
      }, preReminderDelay);
    }

    if (remaining.totalMs > 0) {
      finalReminderTimeoutRef.current = setTimeout(() => {
        if (!finalReminderSentRef.current) {
          finalReminderSentRef.current = true;
          sendNotificationSafe(
            'Czas wyjścia!',
            'To już czas, aby zakończyć pracę!',
            'final-reminder'
          );
        }
      }, remaining.totalMs);
    }

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (preReminderTimeoutRef.current) {
        clearTimeout(preReminderTimeoutRef.current);
      }
      if (finalReminderTimeoutRef.current) {
        clearTimeout(finalReminderTimeoutRef.current);
      }
    };
  }, [startTime, endTime, preReminderMinutes, notificationPermission]);

  // Obsługa zmiany godziny wejścia
  const handleStartTimeChange = (e) => {
    const time = e.target.value;
    setStartTime(time);
    
    if (time) {
      const calculatedEndTime = calculateEndTime(time);
      setEndTime(calculatedEndTime);
      // Zapis do localStorage nastąpi automatycznie przez useEffect
    } else {
      setEndTime(null);
      setTimeRemaining(null);
      setWorkProgress(0);
      // Usuń z localStorage
      localStorage.removeItem('workTimeData');
    }
  };

  // Formatowanie czasu
  const formatTime = (hours, minutes, seconds) => {
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  // Pobierz nazwę dnia tygodnia
  const getDayName = () => {
    const days = ['Niedziela', 'Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota'];
    return days[new Date().getDay()];
  };

  // Pobierz wymagane godziny pracy dla dzisiejszego dnia
  const getWorkHours = () => {
    const dayOfWeek = new Date().getDay();
    switch (dayOfWeek) {
      case 1: return '9.5 godziny';
      case 2:
      case 3:
      case 4: return '8 godzin';
      case 5: return '6.5 godziny';
      default: return '8 godzin (weekend)';
    }
  };

  return (
    <div className={`work-time-calculator ${isPWA ? 'pwa-mode' : ''}`}>
      <div className="calculator-card">
        <h1>Kalkulator Godziny Wyjścia</h1>
        
        <div className="day-info">
          <p className="day-name">{getDayName()}</p>
          <p className="work-hours">Wymagane: {getWorkHours()}</p>
        </div>

        <div className="input-section">
          <label htmlFor="start-time">
            Godzina wejścia:
          </label>
          <input
            id="start-time"
            type="time"
            value={startTime}
            onChange={handleStartTimeChange}
            className="time-input"
          />
        </div>

        {endTime && (
          <div className="result-section">
            <div className="end-time-display">
              <p className="label">Godzina wyjścia:</p>
              <p className="end-time">{endTime}</p>
            </div>

            {timeRemaining && (
              <>
                <div className="timer-display">
                  <p className="label">Pozostało:</p>
                  <p className="timer">
                    {formatTime(
                      timeRemaining.hours,
                      timeRemaining.minutes,
                      timeRemaining.seconds
                    )}
                  </p>
                </div>
                
                <div className="progress-section">
                  <div className="progress-header">
                    <p className="label">Postęp dnia pracy:</p>
                    <p className="progress-percentage">{Math.round(workProgress)}%</p>
                  </div>
                  <div className="progress-bar-container">
                    <div 
                      className="progress-bar" 
                      style={{ width: `${Math.min(Math.max(workProgress, 0), 100)}%` }}
                    ></div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="notification-section">
          <h2>Powiadomienia</h2>

          <div className="notification-controls">
            <div className="permission-control">
              {notificationPermission === 'default' && (
                <button
                  onClick={requestNotificationPermission}
                  className="btn btn-primary"
                >
                  Włącz powiadomienia
                </button>
              )}
              {notificationPermission === 'granted' && (
                <div className="permission-status granted">
                  ✓ Powiadomienia włączone
                </div>
              )}
              {notificationPermission === 'denied' && (
                <div className="permission-status denied">
                  ✗ Powiadomienia zablokowane
                </div>
              )}
            </div>

            {notificationPermission === 'granted' && (
              <div className="pre-reminder-control">
                <label htmlFor="pre-reminder">
                  Przypomnij mi X minut przed wyjściem:
                </label>
                <select
                  id="pre-reminder"
                  value={preReminderMinutes}
                  onChange={(e) => setPreReminderMinutes(Number(e.target.value))}
                  className="reminder-select"
                >
                  <option value={0}>Wyłączone</option>
                  <option value={5}>5 minut</option>
                  <option value={10}>10 minut</option>
                  <option value={15}>15 minut</option>
                  <option value={30}>30 minut</option>
                </select>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkTimeCalculator;

